<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import AppButton from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import { formatFileSize } from '@shared/utils'
import type { OrderDocumentFile } from '../types/orderTypes'

// In-page preview of an order document, with Download. Preview and download
// both come from the one fetched blob. Loading and failure live here — the
// page shows no toast for a failed open.

interface Props {
  visible: boolean
  title: string
  file: OrderDocumentFile | null
  isLoading: boolean
  hasError: boolean
  /** The document has a public link to open in a new tab if the fetch was blocked. */
  hasFallback?: boolean
  /**
   * Public link to frame directly when the blob couldn't be read (CORS).
   * Download then opens the link instead of saving a blob.
   */
  directUrl?: string
}

const props = withDefaults(defineProps<Props>(), {
  hasFallback: false,
  directUrl: '',
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'download'): void
  (e: 'retry'): void
  (e: 'open-fallback'): void
}>()

const objectUrl = ref<string | null>(null)

const releaseUrl = () => {
  if (!objectUrl.value) return
  URL.revokeObjectURL(objectUrl.value)
  objectUrl.value = null
}

// The object URL lives as long as the open preview, not the cached blob.
watch(
  () => [props.visible, props.file] as const,
  ([visible, file]) => {
    releaseUrl()
    if (visible && file) objectUrl.value = URL.createObjectURL(file.blob)
  },
  { immediate: true },
)

onBeforeUnmount(releaseUrl)

// `#toolbar=0&navpanes=0` hides the browser's own PDF chrome under our header.
const frameSrc = computed(() => {
  if (objectUrl.value) return `${objectUrl.value}#toolbar=0&navpanes=0`
  return props.directUrl
})

const canDownload = computed(() => Boolean(props.file || props.directUrl))

const meta = computed(() => {
  const size = formatFileSize(props.file?.sizeBytes)
  return size ? `PDF · ${size}` : 'PDF'
})
</script>

<template>
  <Dialog
    :model-value="visible"
    width="900px"
    :show-close-icon="false"
    :padded="false"
    @update:model-value="emit('close')"
  >
    <Box direction="col" block class="odp">
      <Box align="center" gap="4" block class="odp__header">
        <Box align="center" justify="center" class="odp__icon">
          <PiIcon name="file-pdf" />
        </Box>
        <Box direction="col" gap="1" grow class="odp__title">
          <Text is="h2" type="page-title" truncate>{{ title }}</Text>
          <Text type="caption" color="muted" truncate>{{ file?.fileName ?? meta }}</Text>
        </Box>
        <AppButton
          label="Download"
          icon="download"
          :disabled="!canDownload"
          @click="emit('download')"
        />
        <AppButton
          variant="text"
          color="secondary"
          icon="times"
          aria-label="Close preview"
          @click="emit('close')"
        />
      </Box>

      <Box align="center" justify="center" block class="odp__body">
        <Skeleton v-if="isLoading" width="100%" height="60vh" border-radius="10px" />

        <Box v-else-if="hasError" direction="col" align="center" gap="3" class="odp__error">
          <PiIcon name="exclamation-circle" class="odp__error-icon" />
          <Text type="body" weight="semibold" align="center">
            We couldn't load this document.
          </Text>
          <Box align="center" gap="3">
            <AppButton label="Try again" variant="outline" icon="refresh" @click="emit('retry')" />
            <AppButton
              v-if="hasFallback"
              label="Open in new tab"
              variant="text"
              icon="external-link"
              @click="emit('open-fallback')"
            />
          </Box>
        </Box>

        <!-- Raw <iframe>: the kit has no document-frame citizen yet. §19 exception. -->
        <iframe v-else-if="frameSrc" :src="frameSrc" :title="title" class="odp__frame" />
      </Box>
    </Box>
  </Dialog>
</template>

<style scoped>
.odp {
  max-height: min(88vh, 860px);
}

.odp__header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--p-content-border-color);
  box-sizing: border-box;
}

.odp__icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: var(--p-border-radius-md);
  background: var(--p-pink-100);
  color: var(--p-danger-600);
}

.odp__title {
  min-width: 0;
}

.odp__body {
  flex: 1;
  min-height: 0;
  padding: 1rem;
  background: var(--p-surface-50);
  box-sizing: border-box;
}

.odp__frame {
  width: 100%;
  height: 65vh;
  border: none;
  border-radius: var(--p-border-radius-md);
  background: var(--p-surface-0);
}

.odp__error {
  padding: 3rem 1rem;
}

.odp__error-icon {
  font-size: 28px;
  color: var(--p-danger-500);
}

@media (max-width: 992px) {
  .odp__header {
    padding: 0.75rem 1rem;
  }

  .odp__body {
    padding: 0.5rem;
  }
}
</style>
